import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription, timer } from 'rxjs';
import {
  JournalDictationsService,
  JournalEntry,
  JOURNAL_TRANSCRIBING_PLACEHOLDER
} from '../../../../core/services/journal-dictations.service';
import { ContemplationNotesPanelComponent } from '../contemplation-notes-panel/contemplation-notes-panel.component';

type JournalView = 'list' | 'editor';

const PAGE_SIZE = 20;

@Component({
  selector: 'app-journal-panel',
  standalone: true,
  imports: [CommonModule, ContemplationNotesPanelComponent],
  templateUrl: './journal-panel.component.html',
  styleUrls: ['./journal-panel.component.scss']
})
export class JournalPanelComponent implements OnChanges, OnDestroy {
  /** When true, the parent panel is open — used to refresh entries and poll transcribing items. */
  @Input() isActive = false;

  view: JournalView = 'list';
  entries: JournalEntry[] = [];
  total = 0;
  hasMore = false;
  isLoading = false;
  isLoadingMore = false;
  loadError: string | null = null;
  editingEntry: JournalEntry | null = null;

  private loadSub?: Subscription;
  private loadMoreSub?: Subscription;
  private pollSub?: Subscription;

  constructor(private journalDictations: JournalDictationsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive']?.currentValue) {
      this.loadEntries();
      this.startTranscriptionPolling();
    } else if (changes['isActive']?.currentValue === false) {
      this.stopTranscriptionPolling();
    }
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.loadMoreSub?.unsubscribe();
    this.stopTranscriptionPolling();
  }

  get hasTranscribingEntries(): boolean {
    return this.entries.some(entry => this.journalDictations.isTranscribing(entry));
  }

  openNewEntry(): void {
    this.editingEntry = null;
    this.view = 'editor';
  }

  openEntry(entry: JournalEntry): void {
    this.editingEntry = entry;
    this.view = 'editor';
  }

  backToList(): void {
    this.view = 'list';
    this.editingEntry = null;
    this.loadEntries();
    this.startTranscriptionPolling();
  }

  onEntrySaved(): void {
    this.backToList();
  }

  loadEntries(): void {
    this.loadSub?.unsubscribe();
    this.isLoading = true;
    this.loadError = null;

    this.loadSub = this.journalDictations.listEntries({ limit: PAGE_SIZE }).subscribe({
      next: ({ items, total, hasMore }) => {
        this.entries = items;
        this.total = total;
        this.hasMore = hasMore;
        this.isLoading = false;
        this.syncTranscriptionPolling();
      },
      error: err => {
        console.error(err);
        this.isLoading = false;
        this.loadError = 'Could not load your journal entries.';
      }
    });
  }

  loadMoreEntries(): void {
    if (!this.hasMore || this.isLoading || this.isLoadingMore || this.entries.length === 0) {
      return;
    }

    const oldest = this.entries[this.entries.length - 1];
    const lastCreatedOn = oldest?.created_on;
    if (!lastCreatedOn) {
      return;
    }

    this.loadMoreSub?.unsubscribe();
    this.isLoadingMore = true;

    this.loadMoreSub = this.journalDictations.listEntries({
      limit: PAGE_SIZE,
      last_created_on: lastCreatedOn
    }).subscribe({
      next: ({ items, hasMore }) => {
        const existingIds = new Set(this.entries.map(e => e.id));
        const appended = items.filter(item => !existingIds.has(item.id));
        this.entries = [...this.entries, ...appended];
        this.hasMore = hasMore;
        this.isLoadingMore = false;
      },
      error: err => {
        console.error(err);
        this.isLoadingMore = false;
      }
    });
  }

  onEntriesScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    if (distanceFromBottom < 80) {
      this.loadMoreEntries();
    }
  }

  entryPreview(entry: JournalEntry): string {
    const content = (entry.content ?? '').trim();
    if (!content || content === JOURNAL_TRANSCRIBING_PLACEHOLDER) {
      return 'Transcription in progress…';
    }
    return content.length > 140 ? `${content.slice(0, 140)}…` : content;
  }

  entryTitle(entry: JournalEntry): string {
    return (entry.title ?? '').trim() || 'Untitled entry';
  }

  entryDate(entry: JournalEntry): string {
    const raw = entry.updated_on || entry.created_on;
    if (!raw) return '';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  isTranscribing(entry: JournalEntry): boolean {
    return this.journalDictations.isTranscribing(entry);
  }

  typeLabel(entry: JournalEntry): string {
    return entry.type === 'AUDIO' ? 'Voice' : 'Text';
  }

  private startTranscriptionPolling(): void {
    if (!this.isActive || this.view !== 'list' || !this.hasTranscribingEntries) {
      return;
    }

    this.stopTranscriptionPolling();
    this.pollSub = timer(3000, 3000).subscribe(() => {
      if (!this.isActive || this.view !== 'list') {
        this.stopTranscriptionPolling();
        return;
      }
      this.refreshLatestEntries();
    });
  }

  /** Refresh the newest page without discarding older infinite-scroll pages. */
  private refreshLatestEntries(): void {
    this.journalDictations.listEntries({ limit: PAGE_SIZE }).subscribe({
      next: ({ items, total, hasMore }) => {
        this.total = total;

        const freshById = new Map(items.map(item => [item.id, item]));
        const existingIds = new Set(this.entries.map(e => e.id));

        const updatedEntries = this.entries.map(entry => freshById.get(entry.id) ?? entry);
        const newEntries = items.filter(item => !existingIds.has(item.id));

        this.entries = [...newEntries, ...updatedEntries];
        this.hasMore = this.entries.length < total
          ? (this.entries.length <= PAGE_SIZE ? hasMore : true)
          : false;
        this.syncTranscriptionPolling();
      },
      error: err => console.error(err)
    });
  }

  private syncTranscriptionPolling(): void {
    if (this.hasTranscribingEntries) {
      this.startTranscriptionPolling();
    } else {
      this.stopTranscriptionPolling();
    }
  }

  private stopTranscriptionPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }
}
